import { useCallback, useEffect, useState } from 'react';
import { authenticatedInstance } from '../../../services/index/index.service';

const FAST_PROBE_INTERVAL_MS = 500;
const NORMAL_PROBE_INTERVAL_MS = 1000;
const FAST_PROBE_WINDOW_MS = 3000;

// Summary: POS connectivity truth from navigator online/offline events plus SSE stream health.
export function useConnectionStatus(): {
  isOffline: boolean;
  markStreamDown: () => void;
  markStreamAlive: () => void;
} {
  const [isBrowserOffline, setIsBrowserOffline] = useState(
    () => typeof navigator !== 'undefined' && navigator.onLine === false,
  );
  const [isStreamDown, setIsStreamDown] = useState(false);

  useEffect(() => {
    const goOffline = () => setIsBrowserOffline(true);
    // Regained network clears both signals; a live SSE reconnect also clears via markStreamAlive.
    const goOnline = () => {
      setIsBrowserOffline(false);
      setIsStreamDown(false);
    };
    window.addEventListener('offline', goOffline);
    window.addEventListener('online', goOnline);
    return () => {
      window.removeEventListener('offline', goOffline);
      window.removeEventListener('online', goOnline);
    };
  }, []);

  useEffect(() => {
    if (isBrowserOffline || !isStreamDown) {
      return;
    }

    let cancelled = false;
    let inFlight = false;
    const startedAt = Date.now();
    let timerId: number | null = null;

    const probe = async () => {
      if (cancelled || inFlight) {
        return false;
      }
      inFlight = true;
      try {
        await authenticatedInstance.get('/health', { timeout: 2500 });
        if (!cancelled) {
          setIsStreamDown(false);
        }
        return true;
      } catch {
        // Keep offline state until either SSE recovers or a probe succeeds.
        return false;
      } finally {
        inFlight = false;
      }
    };

    const scheduleNextProbe = () => {
      if (cancelled) {
        return;
      }
      const elapsed = Date.now() - startedAt;
      const delay = elapsed < FAST_PROBE_WINDOW_MS ? FAST_PROBE_INTERVAL_MS : NORMAL_PROBE_INTERVAL_MS;
      timerId = window.setTimeout(() => {
        void probe().then((recovered) => {
          if (!recovered) {
            scheduleNextProbe();
          }
        });
      }, delay);
    };

    void probe().then((recovered) => {
      if (!recovered) {
        scheduleNextProbe();
      }
    });

    return () => {
      cancelled = true;
      if (timerId !== null) {
        window.clearTimeout(timerId);
      }
    };
  }, [isBrowserOffline, isStreamDown]);

  const markStreamDown = useCallback(() => setIsStreamDown(true), []);
  const markStreamAlive = useCallback(() => setIsStreamDown(false), []);

  return { isOffline: isBrowserOffline || isStreamDown, markStreamDown, markStreamAlive };
}
